import React, { createContext, useContext, useMemo, useState } from 'react';
import type { SelectedPath, MenuId } from '../../common/types.tsx';
import { isSelectedPath } from '../../common/utils/selected-path/isSelectedPath.tsx';

type SidebarNav4ControllerType = Readonly<{
	isControlledNav: boolean;
	isControlledPath(menuId: MenuId): boolean;
	setControlledPath(path: SelectedPath | null): void;
}>;

const SidebarNav4ControllerContext = createContext<SidebarNav4ControllerType | null>(null);

type Props = {
	children: React.ReactNode;
	controlledPath?: SelectedPath | null;
};

/**
 * Allows the Sidebar to be programatically controlled (e.g. to be able to expand menu section from code, e.g. for onboarding). This is what `controlled` means in this component.
 * SidebarNav4ControllerProvider component must sit fairly-high in the component-tree, allowing almost any component to control what items in the sidebar are visible.
 *
 * For user expanded and collapsed sections - see SidebarNav4Context and its `isSelected`.
 *
 * Remember to call `setControlledPath(null)` when the Sidebar no longer needs have 'controlled' sections.
 */
export function SidebarNav4ControllerProvider(props: Props): React.JSX.Element {
	const { children, controlledPath: initialControlledPath = null } = props;
	const [controlledPath, setControlledPath] = useState<SelectedPath | null>(initialControlledPath); // null means "not being controlled"

	const sidebarNav4Controller = useMemo(
		() => ({
			// is any path in Navigation a controlled one
			isControlledNav: controlledPath !== null,
			isControlledPath: isSelectedPath(controlledPath ?? []),
			setControlledPath,
		}),
		[controlledPath],
	);

	return (
		<SidebarNav4ControllerContext.Provider value={sidebarNav4Controller}>
			{children}
		</SidebarNav4ControllerContext.Provider>
	);
}

export function useSidebarNav4Controller() {
	const context = useContext(SidebarNav4ControllerContext);

	if (context == null) {
		throw new Error('useSidebarNav4Controller must be used inside a SidebarNav4ControllerContext');
	}

	return context;
}
